import { Radio, Form, ConfigProvider, Flex } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { usePermissions } from 'business/contextProviders/usePermissions';
import {
  useReportProdAnalyzeFilters,
  useReportProdAnalyzeFiltersFormName,
} from 'business/shift-report/report-analyze/pages/production-analyze/providers/filters';
import { GROUP_BY_TYPES_MAP } from 'business/shift-report/report-analyze/pages/production-analyze/types';
import {
  Module_Enum,
  useGetCurrentRingShiftReportQuery,
} from 'generated/graphql';
import { NumberRangeInputWithMessage } from 'ui/form/number-range-input-with-message';
import TemporalitySelectInput, {
  DateSelectionTypeEnum,
} from 'ui/form/temporality-select-input';

export interface ReportProdAnalyzeFiltersProps {
  noDefaultFor?: DateSelectionTypeEnum[];
  teamSwitchEnabled: boolean;
}

export const ReportProdAnalyzeFilters = ({
  noDefaultFor,
  teamSwitchEnabled,
}: ReportProdAnalyzeFiltersProps) => {
  const { t } = useTranslation();
  const formName = useReportProdAnalyzeFiltersFormName();
  const filters = useReportProdAnalyzeFilters();
  const [form] = Form.useForm();

  const { constructionSiteId } = usePermissions();
  const gqlHeaders = useGraphqlApiHeaders(Module_Enum.ShiftReport);
  const { data, error } = useGetCurrentRingShiftReportQuery({
    variables: { constructionSiteId },
    context: {
      headers: gqlHeaders,
    },
  });

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            itemMarginBottom: 0,
          },
        },
      }}
    >
      <Form layout="inline" form={form} name={formName} initialValues={filters}>
        <Form.Item
          layout="vertical"
          name="filterType"
          label={t('input.filterByOptions.label')}
        >
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={[
              {
                label: t('input.filterByOptions.date'),
                value: 'date',
              },
              {
                label: t('input.filterByOptions.ring'),
                value: 'ring',
              },
            ]}
          />
        </Form.Item>

        <Form.Item
          shouldUpdate={(prevValues, curValues) =>
            prevValues.filterType !== curValues.filterType
          }
          noStyle
        >
          {({ getFieldValue }) => {
            const filterType = getFieldValue('filterType');

            if (filterType === 'date') {
              return (
                <Flex>
                  <Form.Item
                    name="dateRange"
                    layout="vertical"
                    label={t('input.dateRange.label')}
                  >
                    <TemporalitySelectInput
                      defaultDate={dayjs()}
                      noDefaultFor={noDefaultFor}
                    />
                  </Form.Item>
                </Flex>
              );
            } else if (filterType === 'ring') {
              return (
                <NumberRangeInputWithMessage
                  name="ringRange"
                  label={t('input.ringRange.label')}
                  leftPlaceholder={t('input.ringRange.startPlaceholder')}
                  rightPlaceholder={t('input.ringRange.endPlaceholder')}
                  message={
                    error
                      ? t('dataAnalysis.errors.currentRingNumberError')
                      : t('dataAnalysis.labels.currentRingNumber', {
                          ringNumber: data?.task.at(0)?.ring,
                        })
                  }
                />
              );
            }
            return null;
          }}
        </Form.Item>
        {teamSwitchEnabled && (
          <Form.Item
            layout="vertical"
            name="groupByType"
            label={t(
              'reportAnalyze.productionAnalyze.input.groupByOptions.label',
            )}
          >
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              options={[
                {
                  label: t(
                    'reportAnalyze.productionAnalyze.input.groupByOptions.shift',
                  ),
                  value: GROUP_BY_TYPES_MAP.shift,
                },
                {
                  label: t(
                    'reportAnalyze.productionAnalyze.input.groupByOptions.team',
                  ),
                  value: GROUP_BY_TYPES_MAP.team,
                },
              ]}
            />
          </Form.Item>
        )}
      </Form>
    </ConfigProvider>
  );
};
